import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'next-i18next'
import useSWR from 'swr'
import { CommunityOwnerBlockedError } from 'shared/errors/community-owner-blocked'
import { useInViewport } from 'shared/hooks/use-in-viewport'
import useUser from 'shared/hooks/use-user'
import { PostInterface } from 'modules/community/types/post-interface'

interface PostAnnounceListResponse<T> {
  items: T[]
}

interface UsePostAnnounceListParams<
  T extends PostInterface,
  ID = T['id'] | null,
  R extends PostAnnounceListResponse<T> = PostAnnounceListResponse<T>,
> {
  fetchPosts: (lastPostId: ID) => Promise<{ data: R }>
  swrKey: string
  resetDeps?: any[]
  getNextPageId?: (data: R) => ID
  getHasMore?: (data: R) => boolean
  initialPageId?: ID
}

export function usePostAnnounceList<
  T extends PostInterface,
  ID = T['id'] | null,
  R extends PostAnnounceListResponse<T> = PostAnnounceListResponse<T>,
>({
  fetchPosts,
  swrKey,
  resetDeps = [],
  getNextPageId,
  getHasMore,
  initialPageId = 0 as unknown as ID,
}: UsePostAnnounceListParams<T, ID, R>) {
  const { t } = useTranslation()
  const { user } = useUser()

  const [lastPostId, setLastPostId] = useState<ID>(initialPageId)
  const [isLoading, setLoading] = useState(false)
  const [isEnd, setIsEnd] = useState(false)

  const { ref: sentinelRef, inViewport } = useInViewport()

  const postsSWRInfo = useSWR<T[]>(swrKey)

  const loadPosts = useCallback(async () => {
    try {
      setLoading(true)
      const response = await fetchPosts(lastPostId)
      const { items } = response.data

      const nextPage: ID | null = getNextPageId
        ? getNextPageId(response.data)
        : items.length > 0
          ? (items[items.length - 1].id as unknown as ID)
          : null

      const hasMore: boolean = getHasMore
        ? getHasMore(response.data)
        : // If the API response includes a "hasMore" flag, use it. We have this flag in most cases.
          // @ts-expect-error: Not all response types have a hasMore flag.
          response.data.hasMore !== undefined
          ? // @ts-expect-error
            response.data.hasMore
          : nextPage !== null

      setLastPostId(nextPage as ID)
      if (!hasMore) {
        setIsEnd(true)
      }

      await postsSWRInfo.mutate((prevPosts = []) => [...prevPosts, ...items], false)
    } catch (e) {
      setIsEnd(true)
      // TODO: Delete when the duplicate httpClient is deleted. Rewrite everything to use useApiWrapperWithErrorValidation.
      if (e instanceof CommunityOwnerBlockedError) {
        return
      }
      toast.error(t('core.error.internal_error_message'))
    } finally {
      setLoading(false)
    }
  }, [lastPostId, fetchPosts, postsSWRInfo.mutate, t, getNextPageId, getHasMore])

  useEffect(() => {
    if (inViewport && !isLoading && !isEnd && user) {
      loadPosts()
    }
  }, [inViewport, isEnd, user])

  useEffect(() => {
    setLastPostId(initialPageId)
    setIsEnd(false)
    postsSWRInfo.mutate(() => [], false)
  }, resetDeps)

  return {
    posts: postsSWRInfo.data || [],
    mutate: postsSWRInfo.mutate,
    loadPosts,
    isLoading,
    isEnd,
    sentinelRef,
  }
}
